import { useEffect, useRef, useState } from "react";

import type { OptimizedTaxonomyFilter } from "@shared/components/CatalogSearch/CatalogSearch";
import { useOptimizedMegaMenuTaxonomiesQuery } from "@shared/data/queries/OptimizedMegaMenuTaxonomiesQuery/OptimizedMegaMenuTaxonomies.query";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import {
  resetCatalogSearchResultTitle,
  setOptimizeMegaMenuCatalogSearchResultTitle,
} from "@shared/reactiveVar/useCatalogSearchResultTitle.store";
import {
  setSelectedOptimizedMegaMenuItem,
  useSelectedOptimizedMegaMenuItem,
} from "@shared/reactiveVar/useSelectedOptimizedMegaMenuItem.store";
import type { Taxonomy } from "@shared/types/graphqlGenerated";

interface TaxonomyWithMetaProps extends Taxonomy {
  isSelected: boolean;
  isDescendantsSelected: boolean;
}

interface SelectedTaxonomy {
  depth: Taxonomy["depth"];
  showSelectedSiblings: boolean;
}

const useOptimizedMegaMenuTaxonomyTree = () => {
  const { activeOptimizedTaxonomies } = useCatalogSearch();

  const [uiTaxonomyTree, setUITaxonomyTree] = useState<TaxonomyWithMetaProps[]>([]);

  const selectedOptimizedMegaMenuItem = useSelectedOptimizedMegaMenuItem();

  const optimizedTaxonomySearchTags = useRef<OptimizedTaxonomyFilter[]>([]);
  const selectedTaxonomiesFromOptimizedMegaMenuItem = useRef<SelectedTaxonomy[]>([]);

  const { data } = useOptimizedMegaMenuTaxonomiesQuery({ variables: { levels: 4 } });

  const allTax: Taxonomy[] = data?.viewer?.taxonomies ?? [];

  /**
   * The taxonomy tree is traversed recursively (depth-first) to set meta
   * properties on each taxonomy node. The meta properties are used to
   * determine if the node will be visible in the UI.
   *
   * Conditions:
   * 1. Only a subset of the taxonomy tree will be visible in the UI
   *
   * 2. Only the taxonomy selected via the mega menu or the typeahead controls
   *    which part of the taxonomy tree subset is visible. Taxonomies selected
   *    from the filter does not
   *
   * 3. The subset will include the selected node, ancestors of the
   *    selected node, and potentially the selected node's siblings and its
   *    descendants
   *
   * 4. Since the tree is traverse depth-first, but the siblings (and sibling's
   *    descendants) of the selected nodes could be visible, the selected
   *    nodes are stored in a useRef to help determine visibility of the
   *    siblings and their descendants
   *
   * 5. The renderCategories function uses the meta properties to determine
   *    which nodes to render
   */
  const setMetaPropsOnTaxonomyTree = (
    taxonomies: TaxonomyWithMetaProps[],
    parentTaxonomy?: TaxonomyWithMetaProps
  ) => {
    taxonomies.forEach((taxonomy: TaxonomyWithMetaProps) => {
      const hasChildren: boolean = (taxonomy?.children?.length ?? 0) > 0;

      if (hasChildren) {
        setMetaPropsOnTaxonomyTree(taxonomy.children as TaxonomyWithMetaProps[], taxonomy);
      }

      if (typeof taxonomy?.isSelected === "boolean") {
        return;
      }

      if (typeof parentTaxonomy !== "undefined" && taxonomy?.isDescendantsSelected) {
        parentTaxonomy.isDescendantsSelected = true;
      }

      if (!hasChildren) {
        taxonomy.isDescendantsSelected = false;
      }

      const isSelected =
        activeOptimizedTaxonomies
          ?.filter((activeTax: OptimizedTaxonomyFilter) => activeTax.type === "search")
          ?.some(
            (activeOptimizedTaxonomy: OptimizedTaxonomyFilter) =>
              activeOptimizedTaxonomy.id === taxonomy.id
          ) ?? false;

      if (isSelected) {
        taxonomy.isSelected = true;
        selectedTaxonomiesFromOptimizedMegaMenuItem.current.push({
          depth: taxonomy.depth,
          showSelectedSiblings: (taxonomy?.children?.length ?? 0) === 0,
        });

        setOptimizeMegaMenuCatalogSearchResultTitle(taxonomy, parentTaxonomy);

        optimizedTaxonomySearchTags.current.push({
          id: taxonomy.id,
          name: taxonomy.name,
          hideFromDisplay: (taxonomy?.children?.length ?? 0) > 0,
          type: "search",
        });

        if (typeof parentTaxonomy !== "undefined") {
          parentTaxonomy.isDescendantsSelected = true;
        }

        return;
      }

      taxonomy.isSelected = false;

      if (
        typeof parentTaxonomy !== "undefined" &&
        typeof parentTaxonomy?.isDescendantsSelected === "undefined"
      ) {
        parentTaxonomy.isDescendantsSelected = false;
      }
    });
  };

  // Runs when the url is out of sync with the selectedOptimizedMegaMenuItem
  useEffect(() => {
    if (
      !activeOptimizedTaxonomies ||
      activeOptimizedTaxonomies.some(taxonomy => taxonomy.name === selectedOptimizedMegaMenuItem)
    ) {
      return;
    }

    const urlOptimizedMegaMenuItem = activeOptimizedTaxonomies.find(
      taxonomy => taxonomy.type === "lock" || taxonomy.type === "search"
    );

    setSelectedOptimizedMegaMenuItem(urlOptimizedMegaMenuItem?.name ?? "");
  }, [selectedOptimizedMegaMenuItem, activeOptimizedTaxonomies]);

  // Runs when a new optimized mega menu option is selected
  useEffect(() => {
    if (selectedOptimizedMegaMenuItem === "" || allTax.length === 0) {
      return;
    }

    selectedTaxonomiesFromOptimizedMegaMenuItem.current = [];
    optimizedTaxonomySearchTags.current = [];
    resetCatalogSearchResultTitle();

    const allTaxWithMetaProps: TaxonomyWithMetaProps[] = JSON.parse(JSON.stringify(allTax));
    setMetaPropsOnTaxonomyTree(allTaxWithMetaProps);
    setUITaxonomyTree(allTaxWithMetaProps);
  }, [selectedOptimizedMegaMenuItem, allTax]);

  // Runs when the page is refreshed with a pre-selected optimized mega menu option
  useEffect(() => {
    if (allTax.length === 0 || uiTaxonomyTree.length !== 0) {
      return;
    }

    const allTaxWithMetaProps: TaxonomyWithMetaProps[] = JSON.parse(JSON.stringify(allTax));
    setMetaPropsOnTaxonomyTree(allTaxWithMetaProps);
    setUITaxonomyTree(allTaxWithMetaProps);
  }, [allTax]);

  return {
    uiTaxonomyTree,
    selectedTaxonomiesFromOptimizedMegaMenuItem,
    optimizedTaxonomySearchTags,
  };
};

export { useOptimizedMegaMenuTaxonomyTree };
